import _ from 'lodash'
import React from 'react'
import { FramesManager } from '../view/frames-manager'
import { CustomFormulas, FormulaRegistry } from '../../helpers/formulas'
import { evaluateExpressionWithValueGetter, preprocess } from '../../helpers/evaluation'
import { FormulaTask, IFormulaTaskProvider, IFormulaTaskResult } from './formula-batch-processor'
import { UI2FormulaProcessingMode } from '../ui2/fields/ui2-formula-field/ui2-formula-field-props'

/**
 * Represents a legacy "formula UI component".
 */
export class FormulaComponentInfo {
  public id: string
  public valueRef: React.MutableRefObject<any>
  public frames: FramesManager
  public uiSchema: any
  public formula: string
  public valuePath: string[]
  public renderKey: string
  public processingMode: UI2FormulaProcessingMode
  public refireCondition: string | undefined
  public inputs: string[] | undefined

  // Tracking state for non-continuous modes
  private hasEvaluated: boolean = false
  private previousConditionValue: boolean = false
  private previousInputValues: Map<string, any> | null = null

  constructor(id, valueRef, frames) {
    this.id = id
    this.valueRef = valueRef
    this.frames = frames
    const rawUiSchema = frames.getContext('uiSchema')
    this.uiSchema = {
      ...rawUiSchema,
      isComputedProperty: _.get(rawUiSchema, 'isComputedProperty', true),
    }
    if (_.has(this.uiSchema, 'value')) {
      this.valuePath = frames.getContext('valuePath')
    }
    // find formula, including those defined in the dataSchema
    const dataSchema = frames.getContext('dataSchema')
    this.formula = _.get(this.uiSchema, 'formula', _.get(dataSchema, 'formula'))
    this.renderKey = this.createRenderKey()
    this.processingMode = _.get(this.uiSchema, 'processingMode', 'continuous')
    this.refireCondition = _.get(this.uiSchema, 'refireCondition')
    this.inputs = _.get(this.uiSchema, 'inputs')
  }

  /**
   * Returns true if this formula should be included in the current batch run,
   * based on the configured processingMode.
   */
  public shouldEvaluate(): boolean {
    switch (this.processingMode) {
      case 'onMount':
        return !this.hasEvaluated

      case 'onCondition': {
        if (!this.refireCondition) return true
        const valueGetter = (key) => this.frames.getValue(key)
        const currentValue = !!evaluateExpressionWithValueGetter(
          preprocess(this.refireCondition),
          valueGetter
        )
        const triggered = !this.previousConditionValue && currentValue
        this.previousConditionValue = currentValue
        return triggered
      }

      case 'onInputsChanged': {
        const paths = this.inputs
        if (!paths?.length) return true
        const current = new Map(paths.map((p) => [p, this.frames.getValue(p)]))
        if (this.previousInputValues === null) {
          // First evaluation: capture baseline and fire to populate the initial value.
          this.previousInputValues = current
          return true
        }
        const changed = paths.some((p) => !_.isEqual(current.get(p), this.previousInputValues!.get(p)))
        if (changed) {
          this.previousInputValues = current
        }
        return changed
      }

      default: // 'continuous'
        return true
    }
  }

  public setValueFromTask(result: IFormulaTaskResult): void {
    this.valueRef.current = result.value

    if (this.processingMode === 'onMount') {
      this.hasEvaluated = true
    }

    if (_.isObject(result.value) && result.value === result.task.originalValue) {
      // without a deep equality check, which might be risky here in terms of
      // either performance or safety (circular references), just force a
      // re-render in this case by creating a new render key for the underlying
      // input field. That way, the input field doesn't retain a stale display
      // value.
      this.renderKey = this.createRenderKey()
    }
  }

  private createRenderKey(): string {
    return _.uniqueId('formula-field-')
  }
}

export type FormulaComponentMap = {
  [id: string]: FormulaComponentInfo
}

/**
 * A collaborator for the entity renderer to register and unregister formula
 * fields to process in batch.
 *
 * This bridges existing formula UI components toward a more model-driven
 * approach.
 */
export class FormulaComponentRegistry implements IFormulaTaskProvider {
  private registrationChangeCallback: () => void
  private registry: FormulaComponentMap

  constructor(registrationChangeCallback: () => void) {
    this.registrationChangeCallback = registrationChangeCallback
    this.registry = {}
  }

  public register(componentInfo: FormulaComponentInfo): void {
    this.registry[componentInfo.id] = componentInfo
    this.registrationChangeCallback()
  }

  public unregister(componentID: string): void {
    _.unset(this.registry, componentID)
    this.registrationChangeCallback()
  }

  getRegisteredFields(): FormulaComponentMap {
    return this.registry
  }

  getFormulaTasks(): FormulaTask[] {
    const registeredFields = _.values(this.registry)
    return _.flatMap(registeredFields, (componentInfo) => {
      if (!componentInfo.shouldEvaluate()) return []
      return [this.createTaskInfo(componentInfo)]
    })
  }

  private createTaskInfo(componentInfo: FormulaComponentInfo): FormulaTask {
    const { formula, valuePath, id, uiSchema, valueRef } = componentInfo
    const originalValue = valueRef.current
    const valueGetter = (key) => {
      const formula =
        FormulaRegistry.bindToLocalContext(key)?.bind({
          applicationContext: FormulaRegistry.getApplicationContext(),
          componentInfo: componentInfo,
        }) ?? CustomFormulas[key]
      const value = componentInfo.frames.getValue(key)
      return _.isNil(value) ? formula : value
    }
    return new FormulaTask({
      formula,
      valuePath,
      valueGetter,
      id,
      originalValue,
      schema: uiSchema,
    })
  }
}
